import { useApolloClient } from "@apollo/client";
import { Avatar, Button, Group, Text, useMantineTheme } from "@mantine/core";
import { GetOrganizationPage } from "src/api/graphql/queries/Organization";
import {
  GetOrganizationPageQuery,
  GetOrganizationPageQueryVariables,
  UpdateAdminMutation,
  UpdateAdminMutationVariables,
  UserRole,
} from "src/generated/graphql";
import { FetchParams, QueryResponse, ColumnConfig } from "@kapex/tables";
import { ArrayElement } from "src/utils/helperTypes";
import { useTogglableTheme } from "src/utils/theme";
import { useCallback, useContext, useEffect } from "react";
import { AppContext } from "src/api/AppContext";
import { BiUserCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { UpdateAdmin } from "src/api/graphql/mutations/Admin";
import { DEFAULT_ORDER, ROUTE_NAMES } from "src/utils/constants";
import ScrollableDataGrid from "src/components/ScrollableDataGrid";
import { FaCheckCircle } from "react-icons/fa";
import OrganizationForm from "./OrganizationForm";
import { useModals } from "@mantine/modals";

export type OrganizationData = ArrayElement<
  GetOrganizationPageQuery["organizationPage"]["items"]
>;

const OrganizationList = () => {
  const navigate = useNavigate();
  const {
    isDarkMode,
    currentAdmin,
    setCurrentPageTitle,
    currentOrganization,
    refetchUserData,
    currentUser,
  } = useContext(AppContext);
  const theme = useMantineTheme();
  const modals = useModals();
  const toggleTheme = useTogglableTheme(isDarkMode, theme);
  const { query, mutate } = useApolloClient();

  const getOrganizations = async (
    fetchParams: FetchParams
  ): Promise<QueryResponse<OrganizationData>> => {
    let response = await query<
      GetOrganizationPageQuery,
      GetOrganizationPageQueryVariables
    >({
      query: GetOrganizationPage,
      variables: {
        pagination: {
          count: fetchParams.count,
          page: fetchParams.page,
          orderBy: fetchParams.orderBy || DEFAULT_ORDER.name,
          filters: fetchParams.filters,
        },
      },
    });

    if (response.data.organizationPage) {
      return {
        rowCount: response.data.organizationPage.totalRows,
        rowData: response.data.organizationPage.items as OrganizationData[],
      };
    }
    return {
      rowData: [],
      rowCount: 0,
    };
  };

  const updateAdminOrg = async (orgId: string) => {
    if (!currentAdmin) {
      return;
    }
    let response = await mutate<
      UpdateAdminMutation,
      UpdateAdminMutationVariables
    >({
      mutation: UpdateAdmin,
      variables: {
        id: currentAdmin.id,
        data: {
          organization: {
            id: orgId,
          },
        },
      },
      fetchPolicy: "no-cache",
    });

    if (response.data?.updateAdmin) {
      refetchUserData();
    }
  };

  useEffect(() => {
    setCurrentPageTitle(ROUTE_NAMES.ORGANIZATION);

    return () => {
      setCurrentPageTitle(undefined);
    };
  }, []);

  const columnConfig = [
    {
      field: "logoUrl",
      name: "",
      sortable: false,
      cellRenderer: (rowItem: OrganizationData) => {
        return rowItem.logoUrl ? (
          <Avatar
            radius="xl"
            sx={{
              ":root": {
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
              },
            }}
            size="sm"
            src={rowItem.logoUrl}
          />
        ) : (
          <div style={{ padding: 0, borderRadius: 18 }}>
            <BiUserCircle size={26} />
          </div>
        );
      },
    },
    {
      field: "name",
      name: "Name",
      filter: {
        field: "name",
        type: "text",
      },
      sortable: true,
      cellRenderer: (rowItem: OrganizationData) => (
        <Text
          weight={500}
          onClick={() => navigate(`/organization/${rowItem.id}`)}
          style={{ cursor: "pointer" }}
        >
          {rowItem.name}
        </Text>
      ),
    },
    {
      field: "createdDate",
      name: "Created",
      filter: {
        field: "createdDate",
        type: "dateRange",
      },
      sortable: true,
      valueParser: (rowItem: OrganizationData) => {
        return new Date(rowItem.createdDate).toDateString();
      },
    },
  ] as ColumnConfig<OrganizationData>[];

  const handleModal = useCallback(() => {
    modals.openModal({
      closeOnClickOutside: false,
      title: "Create Organization",
      children: (
        <OrganizationForm
          handleClose={() => {
            modals.closeAll();
          }}
        />
      ),
    });
  }, []);

  return (
    <div>
      {currentUser?.role === UserRole.Admin ? (
        <Button style={{ marginBottom: 20 }} onClick={handleModal}>
          Create +
        </Button>
      ) : null}
      <ScrollableDataGrid
        topMargin={56}
        columnConfigs={
          currentAdmin
            ? [
                ...columnConfig,
                {
                  field: "id",
                  name: "Active Org",
                  sortable: true,
                  cellRenderer: (rowItem: OrganizationData) => (
                    <Group position="apart">
                      <FaCheckCircle
                        size={24}
                        color={
                          rowItem.id === currentOrganization?.id
                            ? "green"
                            : "#ddd"
                        }
                        style={{
                          opacity:
                            rowItem.id === currentOrganization?.id ? 1 : 0,
                        }}
                      />
                      <Button
                        style={{
                          position: "inherit",
                          opacity:
                            rowItem.id === currentOrganization?.id ? 0 : 1,
                        }}
                        onClick={() => updateAdminOrg(rowItem.id)}
                        radius="md"
                        disabled={rowItem.id === currentOrganization?.id}
                      >
                        Swap
                      </Button>
                    </Group>
                  ),
                },
              ]
            : columnConfig
        }
        dataFetch={getOrganizations}
      />
    </div>
  );
};

export default OrganizationList;
